import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { isOwnerOrAdmin, isTeacher, isSecretary, hasRole } from '../utils/roleHelpers';
import SystemDashboard from './SystemDashboard';
import EmptyState from '../components/EmptyState';
import PaymentAlertsCard from '../components/PaymentAlertsCard';
import { checkEnrollmentRequirements } from '../utils/diagnosticHelper';
import './Dashboard.css';

const DashboardDateFilter = ({ onApply }) => {
  const [fromInput, setFromInput] = useState('');
  const [toInput, setToInput] = useState('');
  const [error, setError] = useState('');

  const applyDates = (from, to) => {
    if (from && to && from > to) {
      setError('La fecha inicial no puede ser posterior a la fecha final');
      return;
    }
    setError('');
    onApply(from, to);
  };

  return (
    <form
      className="dashboard-date-filter"
      onSubmit={(event) => {
        event.preventDefault();
        applyDates(fromInput, toInput);
      }}
    >
      <label>
        Desde
        <input
          type="date"
          value={fromInput}
          max={toInput || undefined}
          onChange={(event) => {
            const nextFrom = event.target.value;
            setFromInput(nextFrom);
            applyDates(nextFrom, toInput);
          }}
        />
      </label>
      <label>
        Hasta
        <input
          type="date"
          value={toInput}
          min={fromInput || undefined}
          onChange={(event) => {
            const nextTo = event.target.value;
            setToInput(nextTo);
            applyDates(fromInput, nextTo);
          }}
        />
      </label>
      <button
        type="button"
        className="dashboard-date-clear"
        onClick={() => {
          setFromInput('');
          setToInput('');
          setError('');
          onApply('', '');
        }}
      >
        Limpiar fechas
      </button>
      {error ? <span className="dashboard-date-error">{error}</span> : null}
    </form>
  );
};

// Dashboard para Profesores
const TeacherDashboard = ({ navigate }) => {
  const { user } = useAuth();
  
  // Obtener grupos del profesor (el backend ya filtra automáticamente)
  const { data: myGroups } = useQuery(
    'teacher-groups',
    async () => {
      try {
        const response = await api.get('/groups', { params: { is_active: true } });
        // El backend ya filtra por teacher_id, así que no necesitamos filtrar de nuevo
        return response.data || [];
      } catch (err) {
        console.error('Error al cargar grupos:', err);
        return [];
      }
    },
    {
      enabled: !!user,
    }
  );

  // Obtener asistencias de hoy
  const { data: todayAttendances } = useQuery(
    'teacher-attendances-today',
    async () => {
      try {
        const today = new Date().toISOString().split('T')[0];
        const response = await api.get('/attendances', { 
          params: { date_from: today, date_to: today } 
        });
        return response.data;
      } catch (err) {
        return [];
      }
    }
  );

  return (
    <div className="dashboard">
      <div className="page-header">
        <h2>Mi Dashboard</h2>
      </div>

      {/* Mis Grupos */}
      <div className="card">
        <h3>Mis Grupos</h3>
        {myGroups && myGroups.length > 0 ? (
          <div className="groups-list">
            {myGroups.map((group) => (
              <div 
                key={group.id} 
                className="group-item" 
                onClick={() => navigate(`/groups/${group.id}`)}
                style={{ cursor: 'pointer' }}
              >
                <div className="group-icon" style={{ backgroundColor: '#3498db20', color: '#3498db' }}>
                  {group.discipline?.name?.[0] || '👨‍🏫'}
                </div>
                <div className="group-info">
                  <h4>{group.name || `Grupo ${group.code}`}</h4>
                  <p className="group-detail">
                    {group.discipline?.name || 'Sin disciplina'} • {group.level?.name || 'Sin nivel'}
                  </p>
                  {group.schedule && (
                    <p className="group-schedule">
                      {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'][group.schedule.day_of_week] || 'N/A'} 
                      {' '}{group.schedule.start_time || ''} 
                      {group.schedule.end_time ? ` - ${group.schedule.end_time}` : ''}
                    </p>
                  )}
                </div>
                <button 
                  className="btn btn-primary btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/attendances?group_id=${group.id}`);
                  }}
                >
                  Marcar Asistencia
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p>No tienes grupos asignados</p>
        )}
      </div>

      {/* Asistencias de Hoy */}
      <div className="card">
        <h3>Asistencias de Hoy</h3>
        <p className="stat-number">{todayAttendances?.length || 0}</p>
        <p className="stat-detail">asistencias marcadas hoy</p>
        <button 
          className="btn btn-primary"
          onClick={() => navigate('/attendances')}
        >
          Ver Todas las Asistencias
        </button>
      </div>
    </div>
  );
};

// Dashboard para Secretarios
const SecretaryDashboard = ({ navigate }) => {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(amount || 0);
  };

  // Obtener estadísticas
  const { data: stats, isLoading: statsLoading, error: statsError } = useQuery(
    ['secretary-stats', dateFrom, dateTo],
    async () => {
      try {
        const response = await api.get('/dashboard/stats', {
          params: {
            ...(dateFrom ? { date_from: dateFrom } : {}),
            ...(dateTo ? { date_to: dateTo } : {}),
          },
        });
        return response.data;
      } catch (err) {
        console.error('[SecretaryDashboard] Error al cargar stats:', err);
        console.error('[SecretaryDashboard] Error response:', err.response?.data);
        return null;
      }
    }
  );

  // Obtener pagos pendientes
  const { data: pendingPayments } = useQuery(
    'secretary-pending-payments',
    async () => {
      try {
        const response = await api.get('/payments', { 
          params: { status: 'pending', limit: 10 } 
        });
        return response.data || [];
      } catch (err) {
        console.error('[SecretaryDashboard] Error al cargar pagos pendientes:', err);
        return [];
      }
    }
  );

  // Obtener pagos vencidos
  const { data: overduePayments } = useQuery(
    'secretary-overdue-payments',
    async () => {
      try {
        const response = await api.get('/payments/overdue', { limit: 10 });
        return response.data || [];
      } catch (err) {
        return [];
      }
    }
  );

  // Obtener matrículas recientes
  const { data: recentEnrollments } = useQuery(
    'secretary-recent-enrollments',
    async () => {
      try {
        const response = await api.get('/enrollments', { 
          params: { status: 'active', limit: 5 } 
        });
        return response.data || [];
      } catch (err) {
        console.error('[SecretaryDashboard] Error al cargar matrículas:', err);
        return [];
      }
    }
  );

  // Obtener estudiantes recientes
  const { data: recentStudents } = useQuery(
    'secretary-recent-students',
    async () => {
      try {
        const response = await api.get('/students', { params: { limit: 5 } });
        return response.data || [];
      } catch (err) {
        console.error('[SecretaryDashboard] Error al cargar estudiantes:', err);
        return [];
      }
    }
  );

  // Obtener estadísticas de sedes
  const { data: locationStats } = useQuery(
    ['secretary-location-stats', dateFrom, dateTo],
    async () => {
      try {
        const response = await api.get('/dashboard/location-stats', {
          params: {
            ...(dateFrom ? { date_from: dateFrom } : {}),
            ...(dateTo ? { date_to: dateTo } : {}),
          },
        });
        return response.data?.locations || [];
      } catch (err) {
        console.error('[SecretaryDashboard] Error al cargar estadísticas de sedes:', err);
        return [];
      }
    }
  );

  // Obtener datos para diagnóstico de matrículas
  const { data: allStudents } = useQuery('students', async () => {
    const response = await api.get('/students');
    return response.data;
  });

  const { data: allDisciplines } = useQuery('disciplines', async () => {
    const response = await api.get('/disciplines');
    return response.data;
  });

  const { data: allGroups } = useQuery('groups', async () => {
    const response = await api.get('/groups');
    return response.data;
  });

  const { data: allLevels } = useQuery('levels', async () => {
    const response = await api.get('/levels');
    return response.data;
  });

  const enrollmentDiagnostic = React.useMemo(() => {
    return checkEnrollmentRequirements({
      students: allStudents || [],
      disciplines: allDisciplines || [],
      groups: allGroups || [],
      levels: allLevels || []
    });
  }, [allStudents, allDisciplines, allGroups, allLevels]);

  // Mostrar estado de carga
  if (statsLoading) {
    return (
      <div className="dashboard">
        <div className="page-header">
          <h2>Dashboard - Secretaría</h2>
        </div>
        <div className="loading">Cargando estadísticas...</div>
      </div>
    );
  }

  // Mostrar error si hay
  if (statsError) {
    return (
      <div className="dashboard">
        <div className="page-header">
          <h2>Dashboard - Secretaría</h2>
        </div>
        <div className="error-container">
          <div className="error">
            <h3>Error al cargar estadísticas</h3>
            <p>{statsError.response?.data?.detail || statsError.message || 'Error desconocido'}</p>
            <button className="btn btn-primary" onClick={() => window.location.reload()}>
              Reintentar
            </button>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="dashboard">
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <div>
            <h2>Dashboard - Secretaría</h2>
            {stats && (
              <p style={{ color: '#7f8c8d', fontSize: '14px', marginTop: '5px' }}>
                Última actualización: {new Date().toLocaleTimeString('es-PE')}
              </p>
            )}
          </div>
        </div>
        <DashboardDateFilter onApply={(from, to) => { setDateFrom(from); setDateTo(to); }} />
      </div>

      {/* Estadísticas de Sedes */}
      {locationStats && locationStats.length > 0 && (
        <div className="stats-section">
          <h3 className="section-title">📍 Estadísticas por Sede</h3>
          <div className="card">
            <div className="locations-stats-grid">
              {locationStats.map((location) => (
                <div key={location.location_id} className="location-stat-card">
                  <div className="location-stat-header">
                    <span className="location-icon">📍</span>
                    <h4>{location.location_name}</h4>
                  </div>
                  <div className="location-stat-body">
                    <div className="location-stat-item">
                      <span className="stat-label">Alumnos:</span>
                      <span className="stat-value">{location.total_students}</span>
                    </div>
                    <div className="location-stat-item">
                      <span className="stat-label">Matrículas Activas:</span>
                      <span className="stat-value">{location.active_enrollments}</span>
                    </div>
                    <div className="location-stat-item">
                      <span className="stat-label">Grupos:</span>
                      <span className="stat-value">{location.active_groups} / {location.total_groups}</span>
                    </div>
                    <div className="location-stat-item">
                      <span className="stat-label">{dateFrom || dateTo ? 'Asistencias (período):' : 'Asistencias (Este Mes):'}</span>
                      <span className="stat-value">{location.attendances_this_month}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Alertas de Pagos */}
      <PaymentAlertsCard />

      {/* Estadísticas Principales - Primera Fila */}
      <div className="stats-section">
        <h3 className="section-title">Resumen General</h3>
        <div className="stats-grid stats-grid-primary">
          <div className="stat-card stat-card-primary" onClick={() => navigate('/students')} style={{ cursor: 'pointer' }}>
            <div className="stat-icon" style={{ backgroundColor: '#3498db20', color: '#3498db' }}>👥</div>
            <div className="stat-info">
              <h3>Total Alumnos</h3>
              <p className="stat-number">{stats?.total_students || 0}</p>
              <p className="stat-detail">{stats?.active_students || 0} activos</p>
            </div>
          </div>

          <div className="stat-card stat-card-primary" onClick={() => navigate('/enrollments')} style={{ cursor: 'pointer' }}>
            <div className="stat-icon" style={{ backgroundColor: '#9b59b620', color: '#9b59b6' }}>📝</div>
            <div className="stat-info">
              <h3>Matrículas Activas</h3>
              <p className="stat-number">{stats?.active_enrollments || 0}</p>
              <p className="stat-detail">de {stats?.total_enrollments || 0} totales</p>
            </div>
          </div>

          <div className="stat-card stat-card-primary" onClick={() => navigate('/payments')} style={{ cursor: 'pointer' }}>
            <div className="stat-icon" style={{ backgroundColor: '#27ae6020', color: '#27ae60' }}>💰</div>
            <div className="stat-info">
              <h3>Ingresos Totales</h3>
              <p className="stat-number">{formatCurrency(stats?.total_revenue)}</p>
              <p className="stat-detail">{formatCurrency(stats?.pending_revenue)} pendientes</p>
            </div>
          </div>
        </div>
      </div>

      {/* Estadísticas de Pagos - Segunda Fila */}
      <div className="stats-section">
        <h3 className="section-title">Estado de Pagos</h3>
        <div className="stats-grid stats-grid-secondary">
          <div className="stat-card stat-card-success" onClick={() => navigate('/payments?status=paid')} style={{ cursor: 'pointer' }}>
            <div className="stat-icon" style={{ backgroundColor: '#16a08520', color: '#16a085' }}>✅</div>
            <div className="stat-info">
              <h3>Pagos Pagados</h3>
              <p className="stat-number">{stats?.paid_payments || 0}</p>
              <p className="stat-detail">{formatCurrency(stats?.total_revenue)}</p>
            </div>
          </div>

          <div className="stat-card stat-card-warning" onClick={() => navigate('/payments?status=pending')} style={{ cursor: 'pointer' }}>
            <div className="stat-icon" style={{ backgroundColor: '#f39c1220', color: '#f39c12' }}>⏳</div>
            <div className="stat-info">
              <h3>Pagos Pendientes</h3>
              <p className="stat-number">{stats?.pending_payments || 0}</p>
              <p className="stat-detail">{formatCurrency(stats?.pending_revenue)}</p>
            </div>
          </div>

          <div className="stat-card stat-card-danger" onClick={() => navigate('/payments?status=overdue')} style={{ cursor: 'pointer' }}>
            <div className="stat-icon" style={{ backgroundColor: '#e74c3c20', color: '#e74c3c' }}>⚠️</div>
            <div className="stat-info">
              <h3>Pagos Vencidos</h3>
              <p className="stat-number">{stats?.overdue_payments || 0}</p>
              <p className="stat-detail">{formatCurrency(stats?.overdue_revenue)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Acciones Rápidas y Alertas */}
      <div className="dashboard-actions-section">
        <div className="card card-actions">
          <h3 className="actions-title">⚡ Acciones Rápidas</h3>
          <div className="quick-actions">
            <button className="btn btn-primary btn-action" onClick={() => navigate('/enrollments?action=create')}>
              <span className="btn-icon">📝</span>
              <span className="btn-text">Nueva Matrícula</span>
            </button>
            <button className="btn btn-primary btn-action" onClick={() => navigate('/payments?action=create')}>
              <span className="btn-icon">💰</span>
              <span className="btn-text">Registrar Pago</span>
            </button>
            <button className="btn btn-secondary btn-action" onClick={() => navigate('/students?action=create')}>
              <span className="btn-icon">👤</span>
              <span className="btn-text">Nuevo Alumno</span>
            </button>
            <button className="btn btn-secondary btn-action" onClick={() => navigate('/students')}>
              <span className="btn-icon">🔍</span>
              <span className="btn-text">Consultar Alumnos</span>
            </button>
          </div>
        </div>
      </div>

      {/* Sección de Pagos y Matrículas */}
      <div className="dashboard-content-section">
        {/* Pagos Vencidos - Urgente */}
        {overduePayments && overduePayments.length > 0 && (
          <div className="card card-alert">
            <div className="card-header">
              <div>
                <h3>⚠️ Pagos Vencidos (Urgente)</h3>
                <p className="card-subtitle">Requieren atención inmediata</p>
              </div>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate('/payments?status=overdue')}>
                Ver todos
              </button>
            </div>
          <div className="payments-list">
            {overduePayments.slice(0, 5).map((payment) => (
              <div 
                key={payment.id} 
                className="payment-item overdue"
                onClick={() => navigate(`/payments/${payment.id}`)}
                style={{ cursor: 'pointer' }}
              >
                <div className="payment-info">
                  <h4>{payment.student?.full_name || payment.enrollment?.student?.full_name || 'Sin nombre'}</h4>
                  <p className="payment-detail">
                    {payment.enrollment?.group?.name || 'Sin grupo'} • 
                    Vence: {new Date(payment.due_date).toLocaleDateString('es-PE')}
                  </p>
                </div>
                <div className="payment-amount overdue">
                  <strong>{formatCurrency(payment.amount - (payment.paid_amount || 0))}</strong>
                  <span>Pendiente</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

        {/* Pagos Pendientes */}
        {pendingPayments && pendingPayments.length > 0 && (
          <div className="card">
            <div className="card-header">
              <div>
                <h3>⏳ Pagos Pendientes</h3>
                <p className="card-subtitle">Por procesar</p>
              </div>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate('/payments?status=pending')}>
                Ver todos
              </button>
            </div>
          <div className="payments-list">
            {pendingPayments.slice(0, 5).map((payment) => (
              <div 
                key={payment.id} 
                className="payment-item"
                onClick={() => navigate(`/payments/${payment.id}`)}
                style={{ cursor: 'pointer' }}
              >
                <div className="payment-info">
                  <h4>{payment.student?.full_name || payment.enrollment?.student?.full_name || 'Sin nombre'}</h4>
                  <p className="payment-detail">
                    {payment.enrollment?.group?.name || 'Sin grupo'} • 
                    Vence: {new Date(payment.due_date).toLocaleDateString('es-PE')}
                  </p>
                </div>
                <div className="payment-amount">
                  <strong>{formatCurrency(payment.amount - (payment.paid_amount || 0))}</strong>
                  <span>Pendiente</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

        {/* Matrículas Recientes - Siempre mostrar */}
        <div className="card">
          <div className="card-header">
            <div>
              <h3>📝 Matrículas Recientes</h3>
              <p className="card-subtitle">Últimas matrículas registradas</p>
            </div>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/enrollments')}>
              Ver todas
            </button>
          </div>
        {recentEnrollments && recentEnrollments.length > 0 ? (
          <div className="enrollments-list">
            {recentEnrollments.map((enrollment) => (
              <div 
                key={enrollment.id} 
                className="enrollment-item"
                onClick={() => navigate(`/enrollments/${enrollment.id}`)}
                style={{ cursor: 'pointer' }}
              >
                <div className="enrollment-icon" style={{ backgroundColor: '#9b59b620', color: '#9b59b6' }}>
                  📝
                </div>
                <div className="enrollment-info">
                  <h4>{enrollment.student?.full_name || (enrollment.student?.first_name && enrollment.student?.last_name ? `${enrollment.student.first_name} ${enrollment.student.last_name}` : 'Sin nombre')}</h4>
                  <p className="enrollment-detail">
                    {enrollment.group?.name || 'Sin grupo'} • 
                    {formatCurrency(enrollment.final_monthly_fee || enrollment.monthly_fee || 0)}/mes
                  </p>
                  <p className="enrollment-status">
                    Estado: <span className={`status-badge status-${enrollment.status}`}>
                      {enrollment.status === 'active' ? 'Activa' : enrollment.status}
                    </span>
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            icon="📝"
            title="Aún no hay matrículas registradas"
            message="Para crear tu primera matrícula, necesitas completar los siguientes requisitos:"
            requirements={enrollmentDiagnostic.requirements}
            primaryAction={enrollmentDiagnostic.allMet ? () => navigate('/enrollments?action=create') : null}
            primaryActionLabel="+ Crear Primera Matrícula"
            hint={enrollmentDiagnostic.allMet 
              ? "¡Todo listo! Puedes crear tu primera matrícula ahora."
              : `Completa ${enrollmentDiagnostic.count.pending} requisito(s) más para poder crear matrículas.`}
            className="compact"
          />
        )}
      </div>

        {/* Estudiantes Recientes - Siempre mostrar */}
        <div className="card">
          <div className="card-header">
            <div>
              <h3>👥 Estudiantes Recientes</h3>
              <p className="card-subtitle">Últimos estudiantes registrados</p>
            </div>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/students')}>
              Ver todos
            </button>
          </div>
        {recentStudents && recentStudents.length > 0 ? (
          <div className="students-list">
            {recentStudents.map((student) => (
              <div 
                key={student.id} 
                className="student-item"
                onClick={() => navigate(`/students/${student.id}`)}
                style={{ cursor: 'pointer' }}
              >
                <div className="student-icon" style={{ backgroundColor: '#3498db20', color: '#3498db' }}>
                  👤
                </div>
                <div className="student-info">
                  <h4>{student.full_name || `${student.first_name || ''} ${student.last_name || ''}`.trim() || 'Sin nombre'}</h4>
                  <p className="student-detail">
                    {student.email || 'Sin email'} • {student.phone || 'Sin teléfono'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            icon="👥"
            title="Aún no hay estudiantes registrados"
            message="Los estudiantes son la base del sistema. Crea tu primer estudiante para comenzar."
            primaryAction={() => navigate('/students?action=create')}
            primaryActionLabel="+ Crear Primer Estudiante"
            hint="Una vez que tengas estudiantes, podrás crear matrículas y gestionar sus actividades."
            className="compact"
          />
        )}
        </div>
      </div>
    </div>
  );
};

// Dashboard completo para Dueños/Administradores
const AdminDashboard = ({ navigate }) => {
  const { user } = useAuth();
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Función para formatear moneda en Soles Peruanos
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(amount || 0);
  };
  
  // Determinar el título según el rol
  const dashboardTitle = hasRole(user, 'owner') 
    ? 'Dashboard - Dueño del Negocio' 
    : 'Dashboard - Administración';
  
  const { data: stats, isLoading, error } = useQuery(
    ['dashboard-stats', user?.tenant_id, dateFrom, dateTo],
    async () => {
      try {
        const response = await api.get('/dashboard/stats', {
          params: {
            ...(dateFrom ? { date_from: dateFrom } : {}),
            ...(dateTo ? { date_to: dateTo } : {}),
          },
        });
        return response.data;
      } catch (err) {
        console.error('Error al cargar estadísticas del dashboard:', err);
        throw err;
      }
    },
    {
      retry: 1,
      onError: (err) => {
        console.error('Error en dashboard stats:', err);
      },
      enabled: !!user?.tenant_id, // Solo ejecutar si el usuario tiene tenant_id
    }
  );

  // Obtener grupos activos para mostrar en el dashboard
  const { data: groups } = useQuery(
    ['dashboard-groups', user?.tenant_id], // Incluir tenant_id en la key
    async () => {
      try {
        const response = await api.get('/groups', { params: { is_active: true, limit: 5 } });
        return response.data;
      } catch (err) {
        console.error('[AdminDashboard] Error al cargar grupos:', err);
        console.error('[AdminDashboard] Error response:', err.response?.data);
        return [];
      }
    },
    {
      retry: 1,
      enabled: !!user?.tenant_id, // Solo ejecutar si el usuario tiene tenant_id
    }
  );

  // Obtener estadísticas del sistema (solo para owners)
  const { data: supportStats } = useQuery(
    ['support-dashboard-stats', user?.tenant_id], // Incluir tenant_id en la key
    async () => {
      try {
        const response = await api.get('/dashboard/support-stats');
        return response.data;
      } catch (err) {
        console.error('Error al cargar estadísticas de soporte:', err);
        return null;
      }
    },
    {
      retry: 1,
      enabled: hasRole(user, 'owner') && !!user?.tenant_id, // Solo cargar si es owner y tiene tenant_id
    }
  );

  // Obtener estadísticas de sedes (para owner y admin)
  const { data: locationStats } = useQuery(
    ['admin-location-stats', user?.tenant_id, dateFrom, dateTo],
    async () => {
      try {
        const response = await api.get('/dashboard/location-stats', {
          params: {
            ...(dateFrom ? { date_from: dateFrom } : {}),
            ...(dateTo ? { date_to: dateTo } : {}),
          },
        });
        return response.data?.locations || [];
      } catch (err) {
        console.error('Error al cargar estadísticas de sedes:', err);
        return [];
      }
    },
    {
      retry: 1,
      enabled: (isOwnerOrAdmin(user) || isSecretary(user)) && !!user?.tenant_id,
    }
  );

  // Obtener datos para diagnóstico de grupos
  const { data: allDisciplines } = useQuery('disciplines', async () => {
    const response = await api.get('/disciplines');
    return response.data;
  });

  const { data: allLevels } = useQuery('levels', async () => {
    const response = await api.get('/levels');
    return response.data;
  });

  const weekdayLabels = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
  const attendanceData = weekdayLabels.map((day, index) => ({
    day,
    asistencias: stats?.attendances_by_weekday?.[index] || 0,
  }));

  const paymentStatusData = stats ? [
    { name: 'Pagados', value: stats.paid_payments, color: '#27ae60' },
    { name: 'Pendientes', value: stats.pending_payments, color: '#f39c12' },
    { name: 'Vencidos', value: stats.overdue_payments, color: '#e74c3c' },
  ].filter(item => item.value > 0) : [];


  if (isLoading) {
    return <div className="loading">Cargando estadísticas...</div>;
  }

  if (error) {
    const errorMessage = error.response?.data?.detail || 
                        (error.code === 'ECONNABORTED' || error.message === 'Network Error' 
                          ? 'No se pudo conectar con el servidor. Verifica que el backend esté corriendo en http://localhost:8000'
                          : error.message || 'Error desconocido');
    
    return (
      <div className="error-container">
        <div className="error">
          <h3>❌ Error al cargar estadísticas</h3>
          <p>{errorMessage}</p>
          <button 
            className="btn btn-primary" 
            onClick={() => window.location.reload()}
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <div>
            <h2>{dashboardTitle}</h2>
            {stats && (
              <p style={{ color: '#7f8c8d', fontSize: '14px', marginTop: '5px' }}>
                Última actualización: {new Date().toLocaleTimeString('es-PE')}
              </p>
            )}
          </div>
        </div>
        <DashboardDateFilter onApply={(from, to) => { setDateFrom(from); setDateTo(to); }} />
      </div>
      
      {/* Estadísticas de Sedes */}
      {locationStats && locationStats.length > 0 && (
        <div className="stats-section" style={{ marginBottom: '30px' }}>
          <h3 className="section-title">📍 Estadísticas por Sede</h3>
          <div className="card">
            <div className="locations-stats-grid">
              {locationStats.map((location) => (
                <div key={location.location_id} className="location-stat-card">
                  <div className="location-stat-header">
                    <span className="location-icon">📍</span>
                    <h4>{location.location_name}</h4>
                  </div>
                  <div className="location-stat-body">
                    <div className="location-stat-item">
                      <span className="stat-label">Alumnos:</span>
                      <span className="stat-value">{location.total_students}</span>
                    </div>
                    <div className="location-stat-item">
                      <span className="stat-label">Matrículas Activas:</span>
                      <span className="stat-value">{location.active_enrollments}</span>
                    </div>
                    <div className="location-stat-item">
                      <span className="stat-label">Grupos:</span>
                      <span className="stat-value">{location.active_groups} / {location.total_groups}</span>
                    </div>
                    <div className="location-stat-item">
                      <span className="stat-label">{dateFrom || dateTo ? 'Asistencias (período):' : 'Asistencias (Este Mes):'}</span>
                      <span className="stat-value">{location.attendances_this_month}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tarjetas de estadísticas principales */}
      <div className="stats-grid">
        <div className="stat-card" onClick={() => navigate('/students')} style={{ cursor: 'pointer' }}>
          <div className="stat-icon" style={{ backgroundColor: '#3498db20', color: '#3498db' }}>👥</div>
          <div className="stat-info">
            <h3>Total Alumnos</h3>
            <p className="stat-number">{stats?.total_students || 0}</p>
            <p className="stat-detail">
              {stats?.active_students || 0} activos • {stats?.inactive_students || 0} inactivos
            </p>
          </div>
        </div>

        <div className="stat-card" onClick={() => navigate('/enrollments')} style={{ cursor: 'pointer' }}>
          <div className="stat-icon" style={{ backgroundColor: '#9b59b620', color: '#9b59b6' }}>📝</div>
          <div className="stat-info">
            <h3>Matrículas Activas</h3>
            <p className="stat-number">{stats?.active_enrollments || 0}</p>
            <p className="stat-detail">de {stats?.total_enrollments || 0} totales</p>
          </div>
        </div>

        <div className="stat-card" onClick={() => navigate('/payments')} style={{ cursor: 'pointer' }}>
          <div className="stat-icon" style={{ backgroundColor: '#27ae6020', color: '#27ae60' }}>💰</div>
          <div className="stat-info">
            <h3>Ingresos Totales</h3>
            <p className="stat-number">{formatCurrency(stats?.total_revenue)}</p>
            <p className="stat-detail">
              {formatCurrency(stats?.pending_revenue)} pendientes
            </p>
          </div>
        </div>

        <div className="stat-card" onClick={() => navigate('/attendances')} style={{ cursor: 'pointer' }}>
          <div className="stat-icon" style={{ backgroundColor: '#e67e2220', color: '#e67e22' }}>✅</div>
          <div className="stat-info">
            <h3>Asistencias Hoy</h3>
            <p className="stat-number">{stats?.attendances_today || 0}</p>
            <p className="stat-detail">
              {stats?.attendances_this_week || 0} esta semana
            </p>
          </div>
        </div>
      </div>

      {/* Segunda fila de estadísticas */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#16a08520', color: '#16a085' }}>⚽</div>
          <div className="stat-info">
            <h3>Disciplinas</h3>
            <p className="stat-number">{stats?.total_disciplines || 0}</p>
          </div>
        </div>

        <div className="stat-card" onClick={() => navigate('/teachers')} style={{ cursor: 'pointer' }}>
          <div className="stat-icon" style={{ backgroundColor: '#3498db20', color: '#3498db' }}>👨‍🏫</div>
          <div className="stat-info">
            <h3>Profesores</h3>
            <p className="stat-number">{stats?.total_teachers || 0}</p>
            <p className="stat-detail">{stats?.active_teachers || 0} activos</p>
          </div>
        </div>

        <div className="stat-card" onClick={() => navigate('/groups')} style={{ cursor: 'pointer' }}>
          <div className="stat-icon" style={{ backgroundColor: '#34495e20', color: '#34495e' }}>👨‍🏫</div>
          <div className="stat-info">
            <h3>Grupos Activos</h3>
            <p className="stat-number">{stats?.active_groups || 0}</p>
            <p className="stat-detail">de {stats?.total_groups || 0} totales</p>
          </div>
        </div>

        <div className="stat-card warning">
          <div className="stat-icon" style={{ backgroundColor: '#e74c3c20', color: '#e74c3c' }}>⚠️</div>
          <div className="stat-info">
            <h3>Pagos Vencidos</h3>
            <p className="stat-number">{stats?.overdue_payments || 0}</p>
            <p className="stat-detail">
              {formatCurrency(stats?.overdue_revenue)}
            </p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#f39c1220', color: '#f39c12' }}>📊</div>
          <div className="stat-info">
            <h3>Asistencias Este Mes</h3>
            <p className="stat-number">{stats?.attendances_this_month || 0}</p>
            <p className="stat-detail">Total: {stats?.total_attendances || 0}</p>
          </div>
        </div>
      </div>

      {/* Gráficos y alertas */}
      <div className="dashboard-content">
        {/* Gráficos */}
        <div className="charts-grid">
          <div className="chart-card">
            <h3>Estado de Pagos</h3>
            {paymentStatusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={paymentStatusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {paymentStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="no-data">No hay datos de pagos</div>
            )}
          </div>

          <div className="chart-card">
            <h3>Asistencias por Día (Esta Semana)</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={attendanceData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="asistencias" fill="#3498db" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Alertas y notificaciones */}
        {stats?.alerts && stats.alerts.length > 0 && (
          <div className="alerts-section">
            <h3>⚠️ Alertas y Notificaciones</h3>
            <div className="alerts-grid">
              {stats.alerts.map((alert, index) => (
                <div
                  key={index}
                  className={`alert-card alert-${alert.type}${alert.link ? ' is-clickable' : ''}`}
                  onClick={alert.link ? () => navigate(alert.link) : undefined}
                  role={alert.link ? 'link' : undefined}
                >
                  <span className="alert-icon">{alert.icon}</span>
                  <span className="alert-message">{alert.message}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Grupos activos - Siempre mostrar */}
        <div className="groups-section">
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3>Grupos Activos Recientes</h3>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate('/groups')}>
                Ver todos
              </button>
            </div>
            {groups && groups.length > 0 ? (
              <div className="groups-list">
                {groups.map((group) => (
                  <div key={group.id} className="group-item" onClick={() => navigate(`/groups/${group.id}`)}>
                    <div className="group-icon" style={{ backgroundColor: group.discipline?.color ? `${group.discipline.color}20` : '#3498db20', color: group.discipline?.color || '#3498db' }}>
                      {group.discipline?.icon || '👨‍🏫'}
                    </div>
                    <div className="group-info">
                      <h4>{group.name || `Grupo ${group.code}`}</h4>
                      <p className="group-detail">
                        {group.discipline?.name || 'Sin disciplina'} • {group.level?.name || 'Sin nivel'}
                      </p>
                      {group.schedule && (
                        <p className="group-schedule">
                          {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'][group.schedule.day_of_week] || 'N/A'} {group.schedule.start_time || ''} {group.schedule.end_time ? `- ${group.schedule.end_time}` : ''}
                        </p>
                      )}
                    </div>
                    <div className="group-status">
                      <span className={`badge ${group.is_active ? 'badge-success' : 'badge-danger'}`}>
                        {group.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState
                icon="👨‍🏫"
                title="Aún no hay grupos creados"
                message="Los grupos organizan a los estudiantes por disciplina, nivel y horario."
                requirements={[
                  {
                    text: 'Al menos una disciplina creada',
                    completed: (allDisciplines?.length || 0) > 0,
                    action: (allDisciplines?.length || 0) === 0 ? '/disciplines?action=create' : null,
                    actionLabel: 'Crear Disciplina',
                    key: 'disciplines'
                  },
                  {
                    text: 'Al menos un nivel configurado',
                    completed: (allLevels?.length || 0) > 0,
                    action: (allLevels?.length || 0) === 0 ? '/levels?action=create' : null,
                    actionLabel: 'Crear Nivel',
                    key: 'levels'
                  }
                ]}
                primaryAction={() => navigate('/groups?action=create')}
                primaryActionLabel="+ Crear Grupo"
                hint="Los grupos son necesarios para poder matricular estudiantes."
                className="compact"
              />
            )}
          </div>
        </div>

        {/* Resumen de Organizaciones */}
        {hasRole(user, 'owner') && supportStats && supportStats?.tenants_summary && supportStats.tenants_summary.length > 0 && (
                <div className="tenants-section">
                  <div className="card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                      <h3>🏢 Organizaciones del Sistema</h3>
                    </div>
                    <div className="tenants-list">
                      {supportStats.tenants_summary.map((tenant) => (
                        <div 
                          key={tenant.id} 
                          className="tenant-item"
                          style={{ cursor: 'default' }}
                        >
                          <div className="tenant-icon" style={{ 
                            backgroundColor: tenant.is_active ? '#27ae6020' : '#e74c3c20', 
                            color: tenant.is_active ? '#27ae60' : '#e74c3c' 
                          }}>
                            🏢
                          </div>
                          <div className="tenant-info">
                            <h4>{tenant.name} ({tenant.code})</h4>
                            <p className="tenant-detail">
                              {tenant.students_count} estudiantes • {tenant.users_count} usuarios • {tenant.enrollments_count} matrículas
                            </p>
                            <p className="tenant-status">
                              Estado: <span className={`status-badge status-${tenant.is_active ? 'active' : 'inactive'}`}>
                                {tenant.is_active ? 'Activo' : 'Inactivo'}
                              </span>
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
        )}

        {/* Información general y contacto con soporte */}
        <div className="info-section">
          <div className="card">
            <h2>Bienvenido al Sistema de Matrículas</h2>
            <p>Gestiona tus academias deportivas de manera eficiente.</p>
            <div className="quick-actions">
              <button className="btn btn-primary" onClick={() => navigate('/students')}>
                + Nuevo Alumno
              </button>
              <button className="btn btn-secondary" onClick={() => navigate('/teachers')}>
                + Nuevo Profesor
              </button>
              <button className="btn btn-secondary" onClick={() => navigate('/enrollments')}>
                Nueva Matrícula
              </button>
              <button className="btn btn-secondary" onClick={() => navigate('/groups')}>
                Nuevo Grupo
              </button>
              <button className="btn btn-secondary" onClick={() => navigate('/payments')}>
                Registrar Pago
              </button>
            </div>
          </div>
          
          {/* Sección de contacto con soporte técnico */}
          {hasRole(user, 'owner') && (
            <div className="card" style={{ 
              marginTop: '20px', 
              background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)', 
              color: '#ffffff',
              border: '2px solid #2980b9'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
                <div style={{ fontSize: '32px' }}>🔧</div>
                <div>
                  <h3 style={{ margin: 0, color: '#ffffff', fontWeight: 'bold', fontSize: '20px' }}>¿Necesitas Ayuda?</h3>
                  <p style={{ margin: '5px 0 0 0', color: '#ffffff', fontSize: '14px' }}>Contacta con nuestro equipo de soporte técnico</p>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#ffffff' }}>
                  <span style={{ fontSize: '20px' }}>📧</span>
                  <span style={{ color: '#ffffff' }}>Email: <strong style={{ color: '#ffffff' }}>contacto.gestion.seven@gmail.com</strong></span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#ffffff' }}>
                  <span style={{ fontSize: '20px' }}>💬</span>
                  <span style={{ color: '#ffffff' }}>Para reportar problemas o solicitar asistencia, contacta al equipo de soporte técnico</span>
                </div>
                <div style={{ 
                  marginTop: '10px', 
                  padding: '15px', 
                  background: 'rgba(255,255,255,0.2)', 
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.3)'
                }}>
                  <p style={{ margin: 0, fontSize: '14px', color: '#ffffff', lineHeight: '1.6' }}>
                    <strong style={{ color: '#ffffff' }}>Nota:</strong> El dashboard de soporte técnico está disponible solo para superusuarios del sistema.
                    Si necesitas acceso a estadísticas del sistema o reportar un problema, utiliza los canales de contacto indicados.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Componente principal que decide qué dashboard mostrar según el rol
const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Determinar qué dashboard mostrar según el rol
  // Prioridad: SuperAdmin (soporte) > Owner/Admin > Secretary > Teacher
  
  // Si es superusuario, mostrar dashboard de gestión del sistema (solo gestión, no datos de negocio)
  if (user?.is_superuser) {
    return <SystemDashboard />;
  }
  
  // Si es dueño o administrador, mostrar dashboard completo
  if (isOwnerOrAdmin(user)) {
    return <AdminDashboard navigate={navigate} />;
  }

  // Si es secretario, mostrar dashboard de secretaría
  if (isSecretary(user)) {
    return <SecretaryDashboard navigate={navigate} />;
  }

  // Si es profesor (y no es owner/admin), mostrar dashboard de profesor
  if (isTeacher(user)) {
    return <TeacherDashboard navigate={navigate} />;
  }

  // Por defecto, dashboard completo
  return <AdminDashboard navigate={navigate} />;
};

export default Dashboard;
